// Translates raw provider error strings (Fal AI, Kling, etc.) into short,
// user-friendly messages that never leak internal vendor names.
//
// Usage:
//   const { title, message } = friendlyGenerationError(item.error, item.mediaType);

const PROVIDER_TERMS = [
  /fal[\s\-_.]*ai/gi,
  /\bfal\.ai\b/gi,
  /\bfal\b(?=[\s_\-:])/gi,
  /kling[\s\-_]*(motion[\s\-_]*control)?(\s*v?\d+(\.\d+)?)?/gi,
  /\bnano[\s\-_]*banana\b/gi,
  /\bbytedance\b/gi,
  /\breplicate\b/gi,
  /\bopenai\b/gi,
  /\bvertex(\s*ai)?\b/gi,
];

function redactProviderNames(text) {
  let out = String(text || "");
  for (const re of PROVIDER_TERMS) {
    out = out.replace(re, "the AI");
  }
  // Collapse repeated "the AI the AI" after redaction.
  out = out.replace(/(\bthe AI\b[\s,]*){2,}/gi, "the AI ");
  return out.trim();
}

function classify(raw) {
  const s = String(raw || "").toLowerCase();
  if (!s) return "unknown";

  // --- Content moderation / safety ---
  if (
    s.includes("nsfw") ||
    s.includes("sensitive") ||
    s.includes("moderation") ||
    s.includes("safety") ||
    s.includes("content_policy") ||
    s.includes("content policy") ||
    s.includes("not safe for work") ||
    s.includes("explicit") ||
    s.includes("violates") ||
    s.includes("forbidden content") ||
    s.includes("flagged") ||
    s.includes("policy violation") ||
    s.includes("risk control")
  ) {
    return "sensitive";
  }

  // --- Video too long / duration limits ---
  if (
    s.includes("too long") ||
    s.includes("max length") ||
    s.includes("maximum length") ||
    s.includes("max duration") ||
    s.includes("maximum duration") ||
    s.includes("video length") ||
    s.includes("video duration") ||
    s.includes("exceeds the maximum") ||
    /duration.*(exceed|too|over)/.test(s) ||
    /(exceed|over).*duration/.test(s) ||
    /\b\d+\s*seconds?\b.*(max|limit|exceed)/.test(s)
  ) {
    return "video-too-long";
  }

  // --- Trim failed ---
  if (s.includes("trim") || s.includes("could not store the trimmed")) {
    return "trim-failed";
  }

  // --- Missing input ---
  if (
    s.includes("missing imageurl") ||
    s.includes("missing motion") ||
    s.includes("missing input") ||
    s.includes("no image provided") ||
    s.includes("image is required") ||
    s.includes("video is required")
  ) {
    return "missing-input";
  }

  // --- Body / person not detected ---
  // Trigger gegen Provider-Validatoren, die einen Personen-/Körper-Detector
  // an Eingabebild oder Motion-Video laufen lassen (z. B. "No complete upper
  // body detected in the video" oder "Person not visible in image").
  if (
    s.includes("upper body") ||
    s.includes("lower body") ||
    s.includes("full body") ||
    s.includes("no body") ||
    s.includes("body detected") ||
    s.includes("body is clearly visible") ||
    s.includes("body visible") ||
    s.includes("person not detected") ||
    s.includes("person not visible") ||
    s.includes("no person") ||
    s.includes("subject not detected") ||
    s.includes("subject not visible")
  ) {
    return "body-not-visible";
  }

  // --- Image issues ---
  if (
    s.includes("decode") ||
    s.includes("invalid image") ||
    s.includes("unable to load image") ||
    s.includes("cannot read image") ||
    s.includes("corrupt image") ||
    s.includes("image format") ||
    s.includes("unsupported image") ||
    s.includes("could not load image") ||
    s.includes("image too small") ||
    s.includes("image too large") ||
    s.includes("face not detected") ||
    s.includes("no face") ||
    s.includes("face detection")
  ) {
    return "image-issue";
  }

  // --- Video format issues ---
  if (
    s.includes("invalid video") ||
    s.includes("unsupported video") ||
    s.includes("corrupt video") ||
    s.includes("video format")
  ) {
    return "video-issue";
  }

  // --- Rate limit / quota ---
  if (
    s.includes("rate limit") ||
    s.includes("rate-limit") ||
    s.includes("too many requests") ||
    s.includes("quota") ||
    s.includes("429")
  ) {
    return "rate-limit";
  }

  // --- Timeout ---
  if (s.includes("timeout") || s.includes("timed out")) {
    return "timeout";
  }

  // --- Network / fetch / download issues ---
  if (
    s.includes("could not fetch") ||
    s.includes("fetch failed") ||
    s.includes("could not download") ||
    s.includes("download finished but file is missing") ||
    s.includes("connection") ||
    s.includes("network") ||
    s.includes("unreachable")
  ) {
    return "network";
  }

  // --- Server / internal ---
  if (
    s.includes("internal server error") ||
    s.includes("500") ||
    s.includes("503") ||
    s.includes("502")
  ) {
    return "server";
  }

  return "unknown";
}

const MESSAGES = {
  sensitive: {
    title: "Content not allowed",
    message:
      "Your image or prompt was flagged as sensitive. Please use a different image or rephrase the prompt and try again. Your credits have been refunded.",
  },
  "video-too-long": {
    title: "Video too long",
    message:
      "The motion video is longer than 30 seconds. Please use a shorter clip (max 30s). Your credits have been refunded.",
  },
  "trim-failed": {
    title: "Couldn't trim the video",
    message:
      "We couldn't trim your motion video to 30 seconds. Please upload a clip that's already shorter than 30 seconds. Your credits have been refunded.",
  },
  "missing-input": {
    title: "Missing input",
    message:
      "Some required input was missing. Please re-upload your character image and motion video, then try again. Your credits have been refunded.",
  },
  "image-issue": {
    title: "Couldn't read your image",
    message:
      "We couldn't process this image. Please try a different photo (clear, well-lit, JPG or PNG). Your credits have been refunded.",
  },
  "body-not-visible": {
    title: "Body not fully visible",
    message:
      "We couldn't clearly see a complete person in your inputs. Please make sure the upper body (head, shoulders and torso) is fully visible and not cropped, blurred or obscured. Try a different image or motion video and try again. Your credits have been refunded.",
  },
  "video-issue": {
    title: "Couldn't read the video",
    message:
      "We couldn't process this motion video. Please try a different clip (MP4 works best). Your credits have been refunded.",
  },
  "rate-limit": {
    title: "Servers are busy",
    message:
      "We're getting a lot of requests right now. Please wait a moment and try again. Your credits have been refunded.",
  },
  timeout: {
    title: "Generation timed out",
    message:
      "The job took too long. Please try again. Your credits have been refunded.",
  },
  network: {
    title: "Connection issue",
    message:
      "We couldn't reach our generation servers. Please try again in a moment. Your credits have been refunded.",
  },
  server: {
    title: "Something went wrong",
    message:
      "Our servers had a hiccup. Please try again. Your credits have been refunded.",
  },
  unknown: {
    title: "Generation failed",
    message:
      "Something went wrong while generating. Please try again with a different image or prompt. Your credits have been refunded.",
  },
};

export function friendlyGenerationError(rawError, mediaType = "image") {
  const kind = classify(rawError);
  const base = MESSAGES[kind] || MESSAGES.unknown;
  const lower = String(rawError || "").toLowerCase();

  // Body-detection: gezielter formulieren, je nachdem ob das Problem im
  // Motion-Video oder im Charakterbild liegt – das hilft dem User direkt
  // zu wissen, was er austauschen soll.
  if (kind === "body-not-visible") {
    if (lower.includes("in the video") || lower.includes("motion video") || lower.includes("driving video")) {
      return {
        kind,
        title: "Body not fully visible in motion video",
        message:
          "We couldn't clearly see a complete person in your motion video. Please pick a clip where the upper body (head, shoulders and torso) is fully visible the whole time and not cropped or hidden. Your credits have been refunded.",
      };
    }
    if (lower.includes("in the image") || lower.includes("input image") || lower.includes("source image") || lower.includes("character image")) {
      return {
        kind,
        title: "Body not fully visible in your photo",
        message:
          "We couldn't clearly see a complete person in your photo. Please use an image where the upper body (head, shoulders and torso) is fully visible and not cropped, blurred or hidden. Your credits have been refunded.",
      };
    }
    return {
      kind,
      title: base.title,
      message: base.message,
    };
  }

  // Tweak a couple of messages depending on whether it's a video or image job
  // so we don't talk about "motion video" for image-only failures.
  if (mediaType !== "video") {
    if (kind === "missing-input") {
      return {
        kind,
        title: base.title,
        message:
          "Some required input was missing. Please re-upload your image and try again. Your credits have been refunded.",
      };
    }
    if (kind === "video-issue" || kind === "video-too-long" || kind === "trim-failed") {
      // Shouldn't normally happen for image-only, but keep a sensible fallback.
      return {
        kind: "unknown",
        title: MESSAGES.unknown.title,
        message: MESSAGES.unknown.message,
      };
    }
  }

  return {
    kind,
    title: base.title,
    message: base.message,
  };
}

// Exported for tests / debugging only.
export const __internals = {
  classify,
  redactProviderNames,
};
